import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import { CardType } from "shared/constants";
import { PaymentMethod } from "shared/entities";

interface PaymentMethodsSectionProps {
  paymentMethods: PaymentMethod[];
  onAddCard: (
    card: Omit<PaymentMethod, "id" | "isDefault" | "clientId" | "createdAt">,
  ) => void;
  onRemoveCard: (id: string) => void;
  onSetDefault: (id: string) => void;
}

const cardColors: Record<CardType, string> = {
  [CardType.VISA]: "#1A1F71",
  [CardType.MASTERCARD]: "#EB001B",
  [CardType.AMEX]: "#007BC1",
  [CardType.DISCOVER]: "#F76F20",
};

const PaymentMethodCard: React.FC<{
  method: PaymentMethod;
  onSetDefault: (id: string) => void;
  onRemove: (id: string) => void;
}> = ({ method, onSetDefault, onRemove }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        border: "1px solid",
        borderColor: method.isDefault ? "#178B8E" : "#E0E0E0",
        borderRadius: 2,
        backgroundColor: method.isDefault
          ? "rgba(23, 139, 142, 0.04)"
          : "white",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CreditCardIcon sx={{ color: cardColors[method.type], fontSize: 32 }} />
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {method.type.charAt(0).toUpperCase() + method.type.slice(1)} ••••{" "}
              {method.last4}
            </Typography>
            {method.isDefault && (
              <Chip
                label="Default"
                size="small"
                sx={{
                  backgroundColor: "#178B8E",
                  color: "white",
                  height: 20,
                  fontSize: "0.7rem",
                }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Expires {String(method.expiryMonth).padStart(2, "0")}/
            {method.expiryYear}
          </Typography>
        </Box>
      </Box>

      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {!method.isDefault && (
          <MenuItem
            onClick={() => {
              onSetDefault(method.id);
              setAnchorEl(null);
            }}
          >
            Set as Default
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            onRemove(method.id);
            setAnchorEl(null);
          }}
          sx={{ color: "error.main" }}
        >
          Remove Card
        </MenuItem>
      </Menu>
    </Box>
  );
};

const CARD_TYPES: CardType[] = [
  CardType.VISA,
  CardType.MASTERCARD,
  CardType.AMEX,
  CardType.DISCOVER,
];

const PaymentMethodsSection: React.FC<PaymentMethodsSectionProps> = ({
  paymentMethods,
  onAddCard,
  onRemoveCard,
  onSetDefault,
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removeDialogId, setRemoveDialogId] = useState<string | null>(null);
  const [newCard, setNewCard] = useState<
    Omit<PaymentMethod, "id" | "isDefault" | "clientId" | "createdAt">
  >({
    type: CardType.VISA,
    last4: "",
    expiryMonth: new Date().getMonth() + 1,
    expiryYear: new Date().getFullYear(),
  });

  const handleAddCard = () => {
    onAddCard(newCard);
    setAddDialogOpen(false);
    setNewCard({
      type: CardType.VISA,
      last4: "",
      expiryMonth: new Date().getMonth() + 1,
      expiryYear: new Date().getFullYear(),
    });
  };

  const handleRemoveConfirm = () => {
    if (removeDialogId) {
      onRemoveCard(removeDialogId);
      setRemoveDialogId(null);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={2}>
        Payment Methods
      </Typography>

      {paymentMethods.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CreditCardIcon
            sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
          />
          <Typography variant="body1" color="text.secondary" mb={2}>
            No payment methods saved. Add a card to book sitters.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{ textTransform: "none", backgroundColor: "#178B8E" }}
          >
            Add Payment Method
          </Button>
        </Box>
      ) : (
        <>
          <Stack spacing={2} mb={2}>
            {paymentMethods.map((method) => (
              <PaymentMethodCard
                key={method.id}
                method={method}
                onSetDefault={onSetDefault}
                onRemove={(id) => setRemoveDialogId(id)}
              />
            ))}
          </Stack>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              textTransform: "none",
              borderColor: "#178B8E",
              color: "#178B8E",
            }}
          >
            Add Payment Method
          </Button>
        </>
      )}

      {/* Add Card Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Payment Method</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Card Type</InputLabel>
              <Select
                value={newCard.type}
                label="Card Type"
                onChange={(e) =>
                  setNewCard((prev) => ({
                    ...prev,
                    type: e.target.value as CardType,
                  }))
                }
              >
                {CARD_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Last 4 Digits"
              value={newCard.last4}
              onChange={(e) =>
                setNewCard((prev) => ({
                  ...prev,
                  last4: e.target.value.replace(/\D/g, "").slice(0, 4),
                }))
              }
              fullWidth
              size="small"
              inputProps={{ maxLength: 4 }}
              placeholder="4242"
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Expiry Month"
                type="number"
                value={newCard.expiryMonth}
                onChange={(e) =>
                  setNewCard((prev) => ({
                    ...prev,
                    expiryMonth: parseInt(e.target.value),
                  }))
                }
                fullWidth
                size="small"
                inputProps={{ min: 1, max: 12 }}
              />
              <TextField
                label="Expiry Year"
                type="number"
                value={newCard.expiryYear}
                onChange={(e) =>
                  setNewCard((prev) => ({
                    ...prev,
                    expiryYear: parseInt(e.target.value),
                  }))
                }
                fullWidth
                size="small"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddDialogOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddCard}
            variant="contained"
            disabled={newCard.last4.length !== 4}
            sx={{ textTransform: "none", backgroundColor: "#178B8E" }}
          >
            Add Card
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog
        open={Boolean(removeDialogId)}
        onClose={() => setRemoveDialogId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Card?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to remove this payment method?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoveDialogId(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveConfirm}
            color="error"
            sx={{ textTransform: "none" }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PaymentMethodsSection;
